import classNames from "classnames";
import React, { useRef, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaField from "../../ui-kit/kuika-field/kuika-field";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { readHelpdeskError } from "./helpdesk-errors";
import { newHelpdeskKey } from "./helpdesk-upload";

export interface HelpdeskSurveyProps {
  ticketNumber: number;
  onSubmitted: () => void;
}

const MAX_COMMENT_LENGTH: number = 800;
const RATINGS: readonly number[] = [1, 2, 3, 4, 5];

/**
 * The survey of a resolved ticket (contract §3.8). "Resolved" asks for a 1–5 rating and a comment;
 * "not resolved" asks for a comment and reopens the ticket.
 */
const HelpdeskSurvey = ({ ticketNumber, onSubmitted }: HelpdeskSurveyProps): React.ReactElement => {
  const [isResolved, setIsResolved] = useState<boolean | null>(null);
  const [rating, setRating] = useState<number>(0);
  // The star under the pointer previews its rating; leaving the stars shows the chosen one again.
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const surveyKey: React.MutableRefObject<string> = useRef<string>(newHelpdeskKey());

  const commentValid: boolean = comment.trim().length > 0 && comment.length <= MAX_COMMENT_LENGTH;
  const canSubmit: boolean = !submitting && isResolved !== null && commentValid && (isResolved === false || rating > 0);
  const shownRating: number = hoveredRating || rating;

  const submit = async (): Promise<void> => {
    if (!canSubmit || isResolved === null) return;
    setSubmitting(true);
    try {
      await HelpdeskService.submitSurvey(
        ticketNumber,
        { isResolved, rating: isResolved ? rating : null, comment: comment.trim() },
        surveyKey.current
      );
      KMainFunctions.displaySuccessNotification(
        isResolved ? "Thank you for your feedback." : "Your ticket has been reopened."
      );
      onSubmitted();
    } catch (error) {
      KMainFunctions.displayErrorNotification(readHelpdeskError(error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kuika-helpdesk-survey" data-testid="helpdeskSurvey">
      <KuikaTextMedium>Was your issue resolved?</KuikaTextMedium>
      <div className="kuika-flex--gap-s" role="group" aria-label="Was your issue resolved?">
        <KuikaButton
          type={isResolved === true ? "primary" : "secondary"}
          color="neutral"
          size="sm"
          active={isResolved === true}
          onClick={(): void => setIsResolved(true)}
          disabled={submitting}
        >
          Yes, resolved
        </KuikaButton>
        <KuikaButton
          type={isResolved === false ? "primary" : "secondary"}
          color="neutral"
          size="sm"
          active={isResolved === false}
          onClick={(): void => setIsResolved(false)}
          disabled={submitting}
        >
          No, reopen it
        </KuikaButton>
      </div>

      {isResolved === true && (
        <div
          className="kuika-helpdesk-survey__rating"
          role="radiogroup"
          aria-label="Rating"
          onMouseLeave={(): void => setHoveredRating(0)}
        >
          {RATINGS.map((value: number): React.ReactElement => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={rating === value}
              aria-label={`${value} of 5`}
              className={classNames("kuika-helpdesk-survey__star", {
                "kuika-helpdesk-survey__star--active": value <= shownRating
              })}
              disabled={submitting}
              onMouseEnter={(): void => {
                if (!submitting) setHoveredRating(value);
              }}
              onClick={(): void => setRating(value)}
            >
              <KuikaSvg name={value <= shownRating ? "star-filled" : "star"} width={20} height={20} />
            </button>
          ))}
        </div>
      )}

      {isResolved !== null && (
        <KuikaField
          label="Comment"
          htmlFor={`helpdesk-survey-comment-${ticketNumber}`}
          counter={{ current: comment.length, max: MAX_COMMENT_LENGTH }}
        >
          <KuikaInput
            id={`helpdesk-survey-comment-${ticketNumber}`}
            type="textarea"
            rows={3}
            value={comment}
            disabled={submitting}
            error={comment.length > MAX_COMMENT_LENGTH}
            onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>, value: string | number): void =>
              setComment(String(value))
            }
          />
        </KuikaField>
      )}

      <div className="kuika-flex-end--gap-s">
        <KuikaButton type="primary" size="sm" onClick={submit} loading={submitting} disabled={!canSubmit}>
          Send survey
        </KuikaButton>
      </div>
    </div>
  );
};

export default HelpdeskSurvey;
